import { ReactNode, useCallback, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import { PasswordResetUser } from "@/types/passwordReset";
import { QuestionRecord } from "@/types/securitySettings";
import { EntityProps } from "@/types/util";
import { ValidationParams } from "@/types/validation";

import { handleChange } from "@/lib/validation";
import { securitySettingsQuestionValidation } from "@/lib/validation/securitySettings";

export default function QuestionForm<
  T extends Pick<PasswordResetUser, "securityQuestion">
>(
  props: EntityProps<
    T,
    Pick<
      PasswordResetUser["securityQuestion"],
      "answer" | "answerConfirmation" | "id" | "label"
    >
  > & {
    answerDisabled?: boolean;
    answerEndAdornment?: ReactNode;
    button?: ReactNode;
    questionDisabled?: boolean;
    questions: QuestionRecord[];
    showV2: boolean;
  }
) {
  const [showAnswerValue, setShowAnswerValue] = useState<boolean>(false);
  const [showAnswerConfirmationValue, setShowAnswerConfirmationValue] =
    useState<boolean>(false);

  function getAnswerValue() {
    const missingQuestion =
      props.answerDisabled && !props.entity?.securityQuestion?.id;

    if (missingQuestion && !showAnswerValue) setShowAnswerValue(true);
    else if (missingQuestion) return "User Does Not Have a Security Question";
    else if (props.entity?.securityQuestion?.blocked)
      return "Answer is locked due to multiple incorrect attempts";
    else return props.entity?.securityQuestion?.answer ?? "";
  }

  const onChangeParams = useCallback<
    () => Pick<
      ValidationParams<
        T,
        Pick<
          T["securityQuestion"],
          "answer" | "answerConfirmation" | "id" | "label"
        >
      >,
      "validation" | "setEntity" | "setErrors"
    >
  >(() => {
    return {
      validation: securitySettingsQuestionValidation,
      setEntity: props.set,
      setErrors: props.setErrors,
    };
  }, [props.set, props.setErrors]);

  const onChange = useCallback(
    (
      value: string,
      field: keyof Pick<
        T["securityQuestion"],
        "answer" | "answerConfirmation" | "id" | "label"
      >,
      parentField: keyof T,
      confirmationValue?: string
    ) => {
      const { validation, setEntity, setErrors } = onChangeParams();

      handleChange<
        T,
        Pick<
          T["securityQuestion"],
          "answer" | "answerConfirmation" | "id" | "label"
        >
      >({
        confirmationValue,
        field,
        parentField,
        setEntity,
        setErrors,
        validation,
        value,
      });
    },
    [onChangeParams]
  );

  if (!props.questions || !props.questions.length) return null;

  return (
    <>
      <FormControl
        id="security-question-control"
        fullWidth
        sx={{ marginBottom: "0.5rem", marginTop: "1rem" }}
        error={!props.entity?.securityQuestion?.id}
      >
        <InputLabel>Security Question</InputLabel>
        {props.questionDisabled && !props.entity?.securityQuestion?.id ? (
          <>
            <Select
              color="error"
              error
              disabled={props.questionDisabled}
              label="Question"
              value=""
            ></Select>
          </>
        ) : (
          <>
            <Select
              disabled={props.questionDisabled}
              label="Question"
              onChange={(event) => {
                const entity = props.questions.find(
                  (q) => q.id === event.target.value
                );

                onChange(event.target.value, "id", "securityQuestion");
                onChange(entity?.label ?? "", "label", "securityQuestion");
              }}
              value={props.entity?.securityQuestion?.id ?? ""}
            >
              {props.questions.map((q: QuestionRecord, index: number) => {
                return (
                  <MenuItem key={`question-${index}`} value={q.id}>
                    {q.label}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText
              sx={{
                display: props.entity?.securityQuestion?.label
                  ? "none"
                  : "block",
              }}
              id="security-question-control-helper"
            >
              {!props.questions.length && props.showV2
                ? "Please complete reCAPTCHA below to load security questions"
                : "Please select a security question"}
            </FormHelperText>
          </>
        )}
      </FormControl>
      <TextField
        label={!props.questionDisabled ? "Set Answer" : "Answer"}
        fullWidth
        onChange={(event) => {
          onChange(event.target.value, "answer", "securityQuestion");
          if (!props.questionDisabled)
            onChange(
              props.entity?.securityQuestion?.answerConfirmation ?? "",
              "answerConfirmation",
              "securityQuestion",
              event.target.value
            );
        }}
        variant="standard"
        type={showAnswerValue ? "text" : "password"}
        error={props.errors.answer !== undefined}
        helperText={props.errors.answer}
        disabled={props.answerDisabled}
        slotProps={{
          input: {
            endAdornment: (
              <Box display="flex" alignItems="center">
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowAnswerValue((prev) => !prev)}
                    edge="end"
                  >
                    {showAnswerValue ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
                {props.answerEndAdornment ?? null}
              </Box>
            ),
          },
        }}
        value={getAnswerValue()}
      />
      {!props.questionDisabled && (
        <TextField
          label="Confirm Answer"
          fullWidth
          onChange={(event) =>
            onChange(
              event.target.value,
              "answerConfirmation",
              "securityQuestion",
              props.entity?.securityQuestion?.answer ?? ""
            )
          }
          variant="standard"
          type={showAnswerConfirmationValue ? "text" : "password"}
          error={props.errors.answerConfirmation !== undefined}
          helperText={props.errors.answerConfirmation}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowAnswerConfirmationValue((prev) => !prev)
                    }
                    edge="end"
                  >
                    {showAnswerConfirmationValue ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          value={props.entity?.securityQuestion?.answerConfirmation ?? ""}
        />
      )}
      {props.button}
    </>
  );
}
